import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { API } from '@/App';
import { SimpleSettings, firstQrParamError } from '@/components/surveyAccess/accessProfileFields';
import { AdvancedSettings } from '@/components/surveyAccess/AdvancedSettings';
import { BranchMappingEditor, cleanMapping } from '@/components/surveyAccess/BranchMappingEditor';
import { BranchImportPanel } from '@/components/surveyAccess/BranchImportPanel';
import { AccessProfileTester } from '@/components/surveyAccess/AccessProfileTester';

const AG_DEFAULTS = { enabled: true, max_failures: 5, window_minutes: 15, lockout_minutes: 15, max_failures_per_network: 20, block_sequential: true };
const DEFAULTS = {
  name: '',
  methods: { qr: true, manual: true },
  required: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  validity_hours: 168,
  single_use: true,
  branch_validation: true,
  match_branch_code: true,
  date_format: 'DDMMYY',
  time_format: 'HHMM',
  qr_date_format: '',
  qr_time_format: '',
  manual: { strip_spaces: true, strip_hyphens: true, numeric_only: true, length: '', segments: [] },
  qr: { code_param: '', branch_param: '', date_param: '', time_param: '', reference_param: '' },
  anti_gaming: AG_DEFAULTS,
  branch_mapping: [],
  is_default: false,
};

const setIn = (obj, [k, ...rest], v) => ({ ...obj, [k]: rest.length ? setIn(obj?.[k] || {}, rest, v) : v });
const num = (v) => (v === '' || v == null ? null : Number(v));

const initForm = (p) => ({
  ...DEFAULTS, ...p,
  methods: { ...DEFAULTS.methods, ...(p.methods || {}) },
  manual: { ...DEFAULTS.manual, ...(p.manual || {}), length: p.manual?.length ?? '' },
  qr: { ...DEFAULTS.qr, ...(p.qr || {}) },
  qr_date_format: p.qr_date_format || '',
  qr_time_format: p.qr_time_format || '',
  anti_gaming: { ...AG_DEFAULTS, ...(p.anti_gaming || {}) },
});

const errorTab = (msg) => (/parameter/i.test(msg) ? 'advanced' : /store code|map/i.test(msg) ? 'branches' : (/profile name/i.test(msg) ? 'name' : 'advanced'));

export const AccessProfileDialog = ({ profile, branches, profileCount = 0, onClose, onSaved }) => {
  const isNew = !profile.profile_id;
  const [form, setForm] = useState(() => initForm(profile));
  const [tab, setTab] = useState('settings');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (path, value) => setForm(f => setIn(f, path, value));
  const mappedCount = (form.branch_mapping || []).filter(m => (m.external_id || '').trim()).length;

  const buildPayload = () => ({
    ...form,
    validity_hours: num(form.validity_hours),
    qr_date_format: form.qr_date_format || null,
    qr_time_format: form.qr_time_format || null,
    anti_gaming: Object.fromEntries(Object.entries(form.anti_gaming).map(([k, v]) => [k, typeof v === 'boolean' ? v : num(v)])),
    manual: {
      ...form.manual,
      length: num(form.manual.length),
      segments: form.manual.segments.map(s => ({ ...s, start: Number(s.start), length: num(s.length) })),
    },
    branch_mapping: cleanMapping(form.branch_mapping, form.methods),
  });

  const showError = (msg) => {
    setError(msg);
    const where = errorTab(msg);
    if (where === 'branches') setTab('branches');
    else { setTab('settings'); if (where === 'advanced') setAdvancedOpen(true); }
  };

  const save = async () => {
    const payload = buildPayload();
    const qrError = firstQrParamError(payload.qr);
    if (qrError) { showError(qrError); return; }
    if (payload.branch_mapping.some(m => !m.access_method)) { showError('Choose QR or Numeric for every branch with a store code.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/access-profiles${isNew ? '' : `/${profile.profile_id}`}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showError(typeof data.detail === 'string' ? data.detail : 'Could not save the Access Profile'); return; }
      toast.success('Access Profile saved');
      onSaved(data);
    } catch { setError('Could not save the Access Profile'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="access-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNew ? 'New Access Profile' : 'Edit Access Profile'}
            {profile.is_default && <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#E0E7FF] text-[#1A3E6F] rounded-full px-2 py-0.5" data-testid="access-profile-default-badge">Default</span>}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="settings" data-testid="access-profile-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="branches" data-testid="access-profile-tab-branches">Branches ({mappedCount})</TabsTrigger>
          </TabsList>
          <TabsContent value="settings" className="space-y-5">
            <div>
              <label className="text-xs font-semibold text-[#334155] block mb-1.5">Profile name</label>
              <Input value={form.name} onChange={(e) => set(['name'], e.target.value)} placeholder="e.g. Receipt codes" data-testid="access-profile-name-input" />
            </div>
            <SimpleSettings form={form} set={set} />
            {profileCount > (isNew ? 0 : 1) && !profile.is_default && (
              <label className="flex items-center gap-2 text-xs text-[#334155] cursor-pointer">
                <Checkbox checked={!!form.is_default} onCheckedChange={(v) => set(['is_default'], !!v)} data-testid="access-profile-default-checkbox" />
                Use as the default profile for new branches
              </label>
            )}
            <AdvancedSettings form={form} set={set} open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <AccessProfileTester buildPayload={() => ({ ...buildPayload(), profile_id: profile.profile_id })} branches={branches} />
            </AdvancedSettings>
          </TabsContent>
          <TabsContent value="branches" className="space-y-4">
            <BranchMappingEditor form={form} set={set} branches={branches} />
            <BranchImportPanel form={form} set={set} branches={branches} />
          </TabsContent>
        </Tabs>
        {error && <p role="alert" className="text-xs text-[#B91C1C] bg-[#FEF2F2] rounded px-3 py-2" data-testid="access-profile-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-3 border-t border-[#E2E8F0]">
          <Button type="button" variant="outline" onClick={onClose} data-testid="access-profile-cancel-btn">Cancel</Button>
          <Button type="button" onClick={save} disabled={saving} className="bg-[#1A3E6F] text-white" data-testid="access-profile-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save profile'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
